import React, { use, useEffect, useRef, useState } from "react";
import {
  Container,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import TpvCallButton from "../components/TpvCallButton";
import CampanaPropinaBlock, { CAMPANA_INICIAL } from "../components/CampanaPropinaBlock";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import EditIcon from "@mui/icons-material/Edit";
import "./FormScreen.css";
import logoSmall from "../assets/logo_small.png";
import logo from "../assets/logo.png";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import LanguageSelector from "../components/LanguageSelector";
import SEO from "../components/SEO";
import { getLocalidadById } from "../api/localidad";
import { getCategoriaEstablecimientoById } from "../api/categoriaEstablecimiento";
import {
  getBusinessById,
  getBusinessByQrId,
  getComisionByBusiness,
  getRatingByBusiness,
} from "../api/business";
import { getTipedsByBusinessId, getTipedByQrId } from "../api/tiped";
import { postTip, updateTip, getTipById } from "../api/tip";
import { ensureTiperId } from "../utils/ensureTiper";
import { getTasas } from "../api/tasa";
import { getTextById, getTextByLanguage } from "../api/text";
import { getCoinById } from "../api/coin";
import { getPaisById } from "../api/pais";
import { event } from "../utils/gtag";
import { SERVER_URL } from "../api";
import PoweredByTipify from "../components/PoweredByTipify";
import EventHeader from "../components/EventHeader";
import BusinessViewSwitcher from "../components/BusinessViewSwitcher";
import { hasRecentMenuVisit } from "../utils/contenidoIntermedio";
import {
  getActiveUnidades,
  buildVisibleDestinoChips,
  isSoloRepartoColectivo,
  shouldShowChipSelector,
  filterTipedsForNuclio,
  sortTipedsForDestino,
} from "../utils/destinoChips";
import { calcularTasaFinal } from "../utils/helpers";
import { isTipPaid, isTipAlreadyPaidError } from "../utils/tipPaymentStatus";
import WalletExpressPay, {
  EXPRESS_TIMEOUT_MS,
  getStripePromise,
} from "../components/WalletExpressPay";
import { getTiperPaymentProfile } from "../api/tiper";
import { postOneTapPayment } from "../api/stripe";
import { KTCookie } from "../utils/cookie";
import {
  resolveTipsFromBusiness,
  getBusinessCoinId,
  isPropinaEditable,
  isValidCustomTipAmount,
  parseCustomTipInput,
  roundCustomTipAmount,
  getCustomTipAmountError,
  formatCustomTipAmount,
  CUSTOM_TIP_ID,
  MIN_CUSTOM_TIP,
  MAX_CUSTOM_TIP,
} from "../utils/importesPropina";
import { construirCaptura } from "../utils/capturaPropina";
import * as embudo from "../utils/embudo";

/** Petición de un dato opcional: sin id o si falla devuelve null, sin cortar la carga. */
async function getOpcional(fetcher, id) {
  if (!id) return null;
  try {
    const res = await fetcher(id);
    return res?.status === 200 ? res.data : null;
  } catch (error) {
    console.warn("Dato del negocio no disponible:", error?.message || error);
    return null;
  }
}

/**
 * Completa el negocio con el nombre de su localidad y su categoría y devuelve
 * su moneda (o null). Ninguno de estos datos es imprescindible: si falta uno se
 * sigue con los demás. Antes un negocio sin categoría dejaba la web sin
 * importes, sin dirección y sin valoración.
 */
async function cargarDatosNegocio(businessData) {
  const [pais, localidad, categoria] = await Promise.all([
    getOpcional(getPaisById, businessData.pais),
    getOpcional(getLocalidadById, businessData.localidad),
    getOpcional(getCategoriaEstablecimientoById, businessData.categoriaEstablecimiento),
  ]);
  // Nunca se muestra un id en lugar del nombre
  businessData.localidad = localidad?.fullName || "";
  businessData.categoriaEstablecimiento = categoria?.name || "";
  const coinId = getBusinessCoinId(businessData, pais?.coin);
  return coinId ? getOpcional(getCoinById, coinId) : null;
}

function getEmptyBusiness() {
  return {
    nombre_comercial: "",
    direccion: "",
    localidad: "",
    codigo_postal: "",
    email: "",
    categoriaEstablecimiento: "",
    telefonos: [],
    entidades_tipeds: [],
    reparto: {},
  };
}

function getEmptyTiped() {
  return {
    nombre: "",
    apellidos: "",
    email: "",
    telefono: "",
    alias: "",
    business: "",
  };
}

function getFormSubtitleKey(nuclio, chips) {
  if (!nuclio) return "form.subtitleBusinessTeam";
  if (chips.length === 1 && chips[0].type === "tiped") {
    return "form.subtitleBusinessSingleTiped";
  }
  if (chips.length <= 1) return "form.subtitleBusinessTeam";
  const types = new Set(chips.map((c) => c.type));
  if (types.size === 1 && types.has("tiped")) {
    return "form.subtitleBusinessTipedsOnly";
  }
  if (types.size === 1 && types.has("unidad")) {
    return "form.subtitleBusinessUnitsOnly";
  }
  return "form.subtitleBusinessChooseDestino";
}

function shuffleArray(list) {
  return [...list].sort(() => Math.random() - 0.5);
}

function hasOrden(value) {
  return (
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value))
  );
}

function sortUnidadesForDestino(unidadesList) {
  const withOrden = [];
  const withoutOrden = [];
  for (const unidad of unidadesList) {
    if (hasOrden(unidad.orden)) withOrden.push(unidad);
    else withoutOrden.push(unidad);
  }
  withOrden.sort((a, b) => Number(a.orden) - Number(b.orden));
  return [...withOrden, ...shuffleArray(withoutOrden)];
}

/** URL absoluta para logo u otro fichero guardado en servidor (misma lógica que EventHeader). */
function resolveStoredMediaUrl(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return trimmed.startsWith("http") ? trimmed : `${SERVER_URL}/${trimmed}`;
}

function applyCustomTipSessionRestore(
  restoredState,
  setIsCustomTipSelected,
  setCustomTipAmount,
) {
  if (
    restoredState.isCustomTipSelected ||
    restoredState.selectedTip?.id === CUSTOM_TIP_ID
  ) {
    setIsCustomTipSelected(true);
  }
  if (
    restoredState.customTipAmount != null &&
    restoredState.customTipAmount !== ""
  ) {
    setCustomTipAmount(String(restoredState.customTipAmount));
  } else if (
    restoredState.selectedTip?.id === CUSTOM_TIP_ID &&
    restoredState.selectedTip?.import
  ) {
    setCustomTipAmount(String(restoredState.selectedTip.import));
  }
}

// Funciones helper para sessionStorage
const SESSION_STORAGE_KEY = "tipify_form_state";

const saveFormStateToSession = (state) => {
  try {
    const stateToSave = {
      rating: state.rating,
      comentario: state.comentario,
      selectedTiped: state.selectedTiped,
      selectedUnidadTrabajo: state.selectedUnidadTrabajo,
      selectedRepartoColectivo: state.selectedRepartoColectivo,
      hasSelectedDestino: state.hasSelectedDestino,
      selectedTip: state.selectedTip,
      isCustomTipSelected: state.isCustomTipSelected,
      customTipAmount: state.customTipAmount,
      payFees: state.payFees,
      campana: state.campana,
      savedTipId: state.savedTipId,
      ratingsDetallados: state.ratingsDetallados,
      recipientId: state.recipientId,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error("Error guardando estado en sessionStorage:", error);
  }
};

const restoreFormStateFromSession = (recipientId) => {
  try {
    const savedState = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!savedState) return null;

    const parsed = JSON.parse(savedState);

    // Verificar que el estado es para el mismo recipientId
    if (parsed.recipientId !== recipientId) {
      // Limpiar si es para otro recipientId
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    // Verificar que el estado no sea muy antiguo (máximo 1 hora)
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - parsed.timestamp > oneHour) {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.error("Error restaurando estado desde sessionStorage:", error);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    return null;
  }
};

const clearFormStateFromSession = () => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (error) {
    console.error("Error limpiando estado de sessionStorage:", error);
  }
};

const EXPRESS_SESSION_KEY = "tipify_express";

/**
 * Pago exprés con Apple Pay / Google Pay en la primera pantalla:
 * - "on": ACTIVO POR DEFECTO para todos (validado con pagos reales en Android
 *   con Google Pay el 18/10/2026; Apple Pay detectado en iPhone). Si la
 *   detección tarda más de EXPRESS_TIMEOUT_MS, se queda la pantalla de siempre.
 * - "diagnostic": ?express=1, además muestra el panel de diagnóstico.
 * - "off": pantalla de siempre. ?express=0 lo apaga en ese navegador (se
 *   recuerda en la sesión) y REACT_APP_EXPRESS_WALLET=off lo apaga para todos.
 * El valor recordado en la sesión es "1" (diagnóstico) o "0" (apagado).
 */
const getExpressMode = () => {
  let param = null;
  let remembered = null;
  try {
    param = new URLSearchParams(window.location.search).get("express");
    if (param === "1" || param === "0") {
      sessionStorage.setItem(EXPRESS_SESSION_KEY, param);
    }
    remembered = sessionStorage.getItem(EXPRESS_SESSION_KEY);
  } catch (error) {
    // sessionStorage no disponible: solo cuenta el parámetro de la URL
  }
  const choice = param === "1" || param === "0" ? param : remembered;
  if (choice === "0") return "off";
  if (choice === "1") return "diagnostic";
  return process.env.REACT_APP_EXPRESS_WALLET === "off" ? "off" : "on";
};

// Pago en un toque con la tarjeta guardada del tiper (mismo interruptor que el exprés)
const TIPER_COOKIE_NAME = "tipifyCookie";
// Si el perfil de pago llega más tarde, se queda la pantalla sin el botón
const ONE_TAP_PROFILE_TIMEOUT_MS = 3000;
const CARD_BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  cartes_bancaires: "Cartes Bancaires",
};
const cardBrandLabel = (brand) =>
  CARD_BRAND_LABELS[brand] ||
  (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Tarjeta");

const FormScreen = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [rating, setRating] = useState(null);
  const [comentario, setComentario] = useState("");
  const [tiper, setTiper] = useState({});
  const [landIn, setLandIn] = useState(new Date());
  const [landOut, setLandOut] = useState(null);
  const [tipeds, setTipeds] = useState([]);
  const [selectedTiped, setSelectedTiped] = useState(null);
  const [selectedUnidadTrabajo, setSelectedUnidadTrabajo] = useState(null);
  const [selectedRepartoColectivo, setSelectedRepartoColectivo] =
    useState(false);
  const [hasSelectedDestino, setHasSelectedDestino] = useState(false);
  const [activeUnidades, setActiveUnidades] = useState([]);
  const [selectedTip, setSelectedTip] = useState(null);
  const [isCustomTipSelected, setIsCustomTipSelected] = useState(false);
  // Primera vez que el cliente elige un importe (captura analítica, embudo)
  const importeElegidoAtRef = useRef(null);
  const [customTipAmount, setCustomTipAmount] = useState("");
  const [customTipModalOpen, setCustomTipModalOpen] = useState(false);
  const [customTipDraft, setCustomTipDraft] = useState("");
  const [customTipInputFocused, setCustomTipInputFocused] = useState(false);
  const [payFees, setPayFees] = useState(false);
  // Campana de propinas (solo si el negocio la ofrece)
  const [campana, setCampana] = useState(CAMPANA_INICIAL);
  const [formErrors, setFormErrors] = useState({
    amount: false,
    destino: false,
    rating: false,
  });
  const ratingSectionRef = useRef(null);
  const amountSectionRef = useRef(null);
  const destinoSectionRef = useRef(null);
  const customTipInputRef = useRef(null);
  const navigate = useNavigate();
  const recipientId = useParams().id;
  const [business, setBusiness] = useState(getEmptyBusiness());
  const [tiped, setTiped] = useState(getEmptyTiped());
  const [tasa, setTasa] = useState({
    percentage: 0,
    percentagePlan: 0,
    minimumAmount: 0,
    maximumAmount: 0,
    _id: "0",
  });
  const [tips, setTips] = useState([]);
  const [coin, setCoin] = useState({});
  const [textCookeis, setTextCoookies] = useState("");
  const [textCookiesContent, setTextCookiesContent] = useState(null);
  const [textLegal, setTextLegal] = useState("");
  const [textLegalContent, setTextLegalContent] = useState(null);
  const [ratingValue, setRatingValue] = useState(null);
  const [isFromBusiness, setIsFromBusiness] = useState(false);
  const [isFromTiped, setIsFromTiped] = useState(false);
  const [ratingsActive, setRatingsActive] = useState(false);
  const [isFromEvent, setIsFromEvent] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [qrCodigo, setQrCodigo] = useState(null);
  const [zona, setZona] = useState(null);
  const [savingRating, setSavingRating] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [ratingsDetallados, setRatingsDetallados] = useState({});
  const [categoriasExpanded, setCategoriasExpanded] = useState(false);
  const [savedTipId, setSavedTipId] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [nuclioNegocio, setNuclioNegocio] = useState(null);
  const [noRecipientId, setNoRecipientId] = useState(false);
  // El negocio está inhabilitado en Tipify: no se pueden dejar propinas
  const [negocioInhabilitado, setNegocioInhabilitado] = useState(false);
  // Pago exprés: modo fijado al cargar ("diagnostic" | "on" | "off") y si el
  // botón oficial de Apple/Google Pay ya está pintado (sustituye a "Continuar").
  const [expressMode] = useState(getExpressMode);
  const [walletReady, setWalletReady] = useState(false);
  const [walletProcessing, setWalletProcessing] = useState(false);
  // Pago en un toque: perfil de pago del tiper de la cookie (null = sin cargar),
  // estado para el panel de diagnóstico y estado del cobro
  // ("idle" | "processing" | "failed": si falla, el botón se oculta).
  const [paymentProfile, setPaymentProfile] = useState(null);
  const [paymentProfileDiag, setPaymentProfileDiag] = useState("sin cargar");
  const [oneTapState, setOneTapState] = useState("idle");
  const [oneTapMessage, setOneTapMessage] = useState(null);
  const oneTapBusyRef = useRef(false);

  // Perfil de pago del tiper de la cookie (solo negocios Stripe). No bloquea
  // la pantalla: si falla o tarda demasiado, se sigue como anónimo.
  useEffect(() => {
    if (isLoading || expressMode === "off") return undefined;
    if (!business?._id || business?.paymentProvider !== "stripe") return undefined;
    const cookieTiperId = KTCookie.getCookie(TIPER_COOKIE_NAME);
    if (!cookieTiperId) {
      setPaymentProfileDiag("sin cookie tipifyCookie (anónimo)");
      return undefined;
    }
    let cancelled = false;
    const startedAt = Date.now();
    setPaymentProfileDiag(`cargando (tiper ${cookieTiperId})…`);
    getTiperPaymentProfile({ tiperId: cookieTiperId })
      .then((res) => {
        if (cancelled) return;
        const data = res?.data || {};
        const ms = Date.now() - startedAt;
        const card = data.canOneTap ? data.paymentMethods?.[0] : null;
        const summary =
          `identified=${!!data.identified} · saveCardConsent=${!!data.saveCardConsent}` +
          ` · canOneTap=${!!data.canOneTap}` +
          (card ? ` · ${card.brand} ${card.last4}` : "") +
          ` (${ms} ms)`;
        // Llegó tarde: no mover la pantalla bajo el dedo (en diagnóstico, sí)
        if (expressMode !== "diagnostic" && ms > ONE_TAP_PROFILE_TIMEOUT_MS) {
          setPaymentProfileDiag(`${summary} · llegó tarde: no se muestra`);
          return;
        }
        setPaymentProfileDiag(summary);
        setPaymentProfile(data);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("No se pudo cargar el perfil de pago del tiper:", err?.message);
        setPaymentProfileDiag(
          `error ${err?.response?.status || ""} ${err?.response?.data?.code || err?.message || ""}`,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isLoading, expressMode, business?._id, business?.paymentProvider]);

  const yaEjecutado = useRef(false);
  const stateRestored = useRef(false);

  useEffect(() => {
    event({
      action: "pantalla_vista",
      category: "Navegación",
      label: "Pantalla Inicial",
    });
  }, []);

  // Restaurar estado desde sessionStorage al montar
  useEffect(() => {
    if (!recipientId || stateRestored.current) return;

    const restoredState = restoreFormStateFromSession(recipientId);
    if (restoredState && restoredState.savedTipId) {
      // Verificar si el tip ya está pagado antes de restaurar
      const checkTipStatus = async () => {
        try {
          const tipRes = await getTipById(restoredState.savedTipId);
          if (tipRes.status === 200 && tipRes.data) {
            const tip = tipRes.data;
            // Si el tip ya está pagado, no restaurar el estado
            if (isTipPaid(tip.estadoPago)) {
              console.log("⚠️ Tip ya está pagado, no se restaura el estado");
              clearFormStateFromSession();
              return;
            }

            // Si el tip está pendiente, restaurar el estado
            console.log(
              "🔄 Restaurando estado del formulario desde sessionStorage",
            );

            if (
              restoredState.rating !== null &&
              restoredState.rating !== undefined
            ) {
              setRating(restoredState.rating);
            }
            if (restoredState.campana) {
              setCampana({ ...CAMPANA_INICIAL, ...restoredState.campana });
            }
            if (restoredState.comentario) {
              setComentario(restoredState.comentario);
            }
            if (restoredState.selectedTiped) {
              setSelectedTiped(restoredState.selectedTiped);
            }
            if (restoredState.selectedUnidadTrabajo) {
              setSelectedUnidadTrabajo(restoredState.selectedUnidadTrabajo);
            }
            if (restoredState.selectedRepartoColectivo) {
              setSelectedRepartoColectivo(
                restoredState.selectedRepartoColectivo,
              );
            }
            if (restoredState.hasSelectedDestino !== undefined) {
              setHasSelectedDestino(restoredState.hasSelectedDestino);
            } else if (restoredState.hasSelectedTipedOption !== undefined) {
              setHasSelectedDestino(restoredState.hasSelectedTipedOption);
            }
            if (restoredState.selectedTip) {
              setSelectedTip(restoredState.selectedTip);
            }
            applyCustomTipSessionRestore(
              restoredState,
              setIsCustomTipSelected,
              setCustomTipAmount,
            );
            if (restoredState.payFees !== undefined) {
              setPayFees(restoredState.payFees);
            }
            setSavedTipId(restoredState.savedTipId);
            yaEjecutado.current = true; // Evitar crear otro tip
            if (restoredState.ratingsDetallados) {
              setRatingsDetallados(restoredState.ratingsDetallados);
            }

            stateRestored.current = true;
          }
        } catch (error) {
          console.error("Error verificando estado del tip:", error);
          // Si hay error, restaurar el estado de todas formas
          console.log(
            "🔄 Restaurando estado del formulario desde sessionStorage (sin verificación)",
          );

          if (
            restoredState.rating !== null &&
            restoredState.rating !== undefined
          ) {
            setRating(restoredState.rating);
          }
          if (restoredState.campana) {
            setCampana({ ...CAMPANA_INICIAL, ...restoredState.campana });
          }
          if (restoredState.comentario) {
            setComentario(restoredState.comentario);
          }
          if (restoredState.selectedTiped) {
            setSelectedTiped(restoredState.selectedTiped);
          }
          if (restoredState.selectedUnidadTrabajo) {
            setSelectedUnidadTrabajo(restoredState.selectedUnidadTrabajo);
          }
          if (restoredState.selectedRepartoColectivo) {
            setSelectedRepartoColectivo(restoredState.selectedRepartoColectivo);
          }
          if (restoredState.hasSelectedDestino !== undefined) {
            setHasSelectedDestino(restoredState.hasSelectedDestino);
          } else if (restoredState.hasSelectedTipedOption !== undefined) {
            setHasSelectedDestino(restoredState.hasSelectedTipedOption);
          }
          if (restoredState.selectedTip) {
            setSelectedTip(restoredState.selectedTip);
          }
          applyCustomTipSessionRestore(
            restoredState,
            setIsCustomTipSelected,
            setCustomTipAmount,
          );
          if (restoredState.payFees !== undefined) {
            setPayFees(restoredState.payFees);
          }
          setSavedTipId(restoredState.savedTipId);
          yaEjecutado.current = true;
          if (restoredState.ratingsDetallados) {
            setRatingsDetallados(restoredState.ratingsDetallados);
          }

          stateRestored.current = true;
        }
      };

      checkTipStatus();
    } else if (restoredState && !restoredState.savedTipId) {
      // Si hay estado pero no savedTipId, restaurar de todas formas
      console.log(
        "🔄 Restaurando estado del formulario desde sessionStorage (sin tipId)",
      );

      if (restoredState.rating !== null && restoredState.rating !== undefined) {
        setRating(restoredState.rating);
      }
      if (restoredState.campana) {
        setCampana({ ...CAMPANA_INICIAL, ...restoredState.campana });
      }
      if (restoredState.comentario) {
        setComentario(restoredState.comentario);
      }
      if (restoredState.selectedTiped) {
        setSelectedTiped(restoredState.selectedTiped);
      }
      if (restoredState.selectedUnidadTrabajo) {
        setSelectedUnidadTrabajo(restoredState.selectedUnidadTrabajo);
      }
      if (restoredState.selectedRepartoColectivo) {
        setSelectedRepartoColectivo(restoredState.selectedRepartoColectivo);
      }
      if (restoredState.hasSelectedDestino !== undefined) {
        setHasSelectedDestino(restoredState.hasSelectedDestino);
      } else if (restoredState.hasSelectedTipedOption !== undefined) {
        setHasSelectedDestino(restoredState.hasSelectedTipedOption);
      }
      if (restoredState.selectedTip) {
        setSelectedTip(restoredState.selectedTip);
      }
      applyCustomTipSessionRestore(
        restoredState,
        setIsCustomTipSelected,
        setCustomTipAmount,
      );
      if (restoredState.payFees !== undefined) {
        setPayFees(restoredState.payFees);
      }
      if (restoredState.ratingsDetallados) {
        setRatingsDetallados(restoredState.ratingsDetallados);
      }

      stateRestored.current = true;
    }
  }, [recipientId]);

  useEffect(() => {
    // Verificar si ya hay un savedTipId restaurado desde sessionStorage
    if (savedTipId && yaEjecutado.current) {
      return; // Ya existe un tip, no crear otro
    }

    //creat tip desde cero pendiente sin pago
    const createTip = async () => {
      try {
        // Verificar si ya existe un tip pendiente para este evento
        if (savedTipId) {
          try {
            const existingTip = await getTipById(savedTipId);
            if (existingTip.status === 200 && existingTip.data) {
              console.log("✅ Tip existente encontrado, no se crea uno nuevo");
              yaEjecutado.current = true;
              return;
            }
          } catch (error) {
            // Si no existe, continuar con la creación
            console.log("Tip no encontrado, se creará uno nuevo");
          }
        }

        const tiperId = await ensureTiperId();
        let tip = {
          evento: eventData._id,
          qrCodigo: qrCodigo,
          zona: zona,
          business: business._id,
          landIn: landIn,
          estadoValorado: "pending",
          tiper: tiperId,
        };

        let res;
        // Crear nuevo tip
        res = await postTip(tip);
        if (res.status === 201 || res.status === 200) {
          setSavedTipId(res.data.id);
          yaEjecutado.current = true;
        }
      } catch (error) {
        console.error("Error al guardar rating:", error);
        Swal.fire({
          icon: "error",
          title: t("common.oops"),
          text: t("formErrors.saveRating"),
        });
      }
    };

    if (business._id && qrCodigo && eventData && zona) {
      if (yaEjecutado.current) return;
      createTip();
    }

    //crear tip desde cero pendiente con pago
    const createTipPago = async () => {
      try {
        // Verificar si ya existe un tip pendiente
        if (savedTipId) {
          try {
            const existingTip = await getTipById(savedTipId);
            if (existingTip.status === 200 && existingTip.data) {
              console.log("✅ Tip existente encontrado, no se crea uno nuevo");
              yaEjecutado.current = true;
              return;
            }
          } catch (error) {
            // Si no existe, continuar con la creación
            console.log("Tip no encontrado, se creará uno nuevo");
          }
        }

        const tiperId = await ensureTiperId();
        let tip = {
          business: business._id,
          landIn: landIn,
          estadoPago: "pending",
          tiper: tiperId,
        };
        if (nuclioNegocio?._id) {
          tip.nuclioNegocio = nuclioNegocio._id;
        }

        let res;
        // Crear nuevo tip
        res = await postTip(tip);
        if (res.status === 201 || res.status === 200) {
          setSavedTipId(res.data.id);
          yaEjecutado.current = true;
        }
      } catch (error) {
        console.error("Error al guardar rating:", error);
        Swal.fire({
          icon: "error",
          title: t("common.oops"),
          text: t("formErrors.saveRating"),
        });
      }
    };

    if (business._id && !eventData) {
      if (yaEjecutado.current) return;
      createTipPago();
    }
  }, [business, nuclioNegocio, qrCodigo, eventData, zona, savedTipId, t]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      // Lo que no depende del QR se pide a la vez y sin esperar: los textos
      // legales solo hacen falta al abrir sus ventanas, y las tasas antes de
      // aplicar la comisión del negocio (ver aplicarComision).
      getTextById("679c9c23474dd68f9a1080d9")
        .then((textResCookies) => {
          if (textResCookies.status === 200) {
            setTextCookiesContent(textResCookies.data.content);
            setTextCoookies(getTextByLanguage(textResCookies.data.content));
          }
        })
        .catch((err) => console.warn("No se pudo cargar el texto de cookies:", err?.message));
      getTextById("679c9bb4474dd68f9a1080c6")
        .then((textResLegal) => {
          if (textResLegal.status === 200) {
            setTextLegalContent(textResLegal.data.content);
            setTextLegal(getTextByLanguage(textResLegal.data.content));
          }
        })
        .catch((err) => console.warn("No se pudo cargar el texto legal:", err?.message));
      const tasaCargada = getTasas()
        .then((tasaRes) => {
          if (tasaRes.status === 200) {
            setTasa(
              tasaRes.data.length
                ? tasaRes.data.reduce((max, item) =>
                    new Date(item.createdAt) > new Date(max.createdAt)
                      ? item
                      : max,
                  )
                : {},
            );
          }
        })
        .catch((err) => console.warn("No se pudieron cargar las tasas:", err?.message));

      // Tasa calculada en el servidor (coste operativo del negocio + plan):
      // los negocios con comercio Redsys propio usan la tarifa de su banco.
      // Va después de las tasas generales para no quedar pisada por ellas.
      const aplicarComision = async (comisionRes) => {
        await tasaCargada;
        if (comisionRes.status !== 200) return;
        const comisionData = comisionRes.data;
        if (comisionData.tasa) {
          setTasa((prev) => ({ ...prev, ...comisionData.tasa }));
        } else if (comisionData.comision) {
          setTasa((prev) => ({
            ...prev,
            percentagePlan: comisionData.comision,
          }));
        }
      };

      // Datos del negocio que solo dependen de él: a la vez, no uno tras otro
      const cargarNegocio = async (businessData, { conTipeds = true } = {}) => {
        const [coinData, ratingRes, comisionRes, tipedsRes] = await Promise.all([
          // Localidad, categoría y moneda: si falta alguna se sigue con lo demás
          cargarDatosNegocio(businessData),
          getRatingByBusiness(businessData._id).catch((err) => {
            console.warn("No se pudo obtener el rating del negocio:", err);
            return null;
          }),
          getComisionByBusiness(businessData._id),
          conTipeds ? getTipedsByBusinessId(businessData._id) : null,
        ]);
        if (coinData) setCoin(coinData);
        setTips(resolveTipsFromBusiness(businessData, coinData));
        if (ratingRes?.status === 200) {
          setRatingValue(ratingRes.data.rating?.toFixed(1));
        }
        await aplicarComision(comisionRes);
        return tipedsRes;
      };

      try {
        if (
          !recipientId ||
          recipientId === "undefined" ||
          recipientId === "null"
        ) {
          console.log("ℹ️ No hay recipientId. Mostrando mensaje informativo.");
          setNoRecipientId(true);
          setIsLoading(false);
          return;
        }

        setNoRecipientId(false);

        let responseData = null;

        // El QR puede ser de un evento, de un negocio o de un empleado: se
        // pregunta por los tres a la vez y se elige en ese orden, como antes.
        // Solo el de eventos registra el escaneo; los otros dos solo leen.
        const [eventRes, businessRes, tipedRes] = await Promise.all([
          import("../api/event")
            .then(({ getEventByQrCode }) => getEventByQrCode(recipientId))
            .catch((eventError) => {
              console.log("FormScreen - Error al buscar evento:", eventError);
              return null;
            }),
          getBusinessByQrId(recipientId).catch(() => null),
          getTipedByQrId(recipientId).catch(() => null),
        ]);

        if (eventRes?.status === 410) {
          setNegocioInhabilitado(true);
          setIsLoading(false);
          return;
        }
        if (eventRes?.status === 200) {
          responseData = eventRes.data;
        }

        if (!responseData && businessRes) {
          try {
            if (businessRes.status === 410) {
              setNegocioInhabilitado(true);
              setIsLoading(false);
              return;
            }
            if (businessRes.status === 200) {
              responseData = businessRes.data;
              // Contenido intermedio activo (carta/página/agenda):
              // solo redirigir en la 1ª lectura. Si viene del tab de propinas
              // (?ratings=true) o ya vio la carta hace poco (2ª lectura),
              // se queda en propinas con el switcher para volver a la carta.
              if (
                responseData.tipo === "business" &&
                responseData.business?.contenidoIntermedio?.activo
              ) {
                const urlParams = new URLSearchParams(window.location.search);
                const skipContentRedirect =
                  urlParams.get("ratings") === "true";
                const yaVioContenido = hasRecentMenuVisit(
                  responseData.business?._id,
                );
                if (!skipContentRedirect && !yaVioContenido) {
                  setIsLoading(false);
                  navigate(`/business/${recipientId}`, { replace: true });
                  return;
                }
              }
            }
          } catch (businessError) {
            console.log("No es un QR de business, intentando tiped...");
          }
        }

        if (responseData) {
          if (responseData.tipo === "evento") {
            // Si el evento tiene contenido intermedio activo, redirigir a EventLandingPage
            // PERO solo si no venimos de una redirección explícita desde el botón de valoración
            // Verificamos si hay un parámetro de query que indique que queremos ir directamente a valoración
            const urlParams = new URLSearchParams(window.location.search);
            const skipRedirect = urlParams.get("ratings") === "true";

            if (
              responseData.evento?.contenidoIntermedio?.activo &&
              !skipRedirect
            ) {
              setIsLoading(false);
              navigate(`/event/${recipientId}`, { replace: true });
              return;
            }

            setIsFromEvent(true);
            setIsFromBusiness(true);
            setIsFromTiped(false);
            setNuclioNegocio(null);
            setEventData(responseData.evento);
            setQrCodigo(responseData.qrInfo?.codigo || null);
            setZona(responseData.qrInfo?.zona || null);

            const DEFAULT_CATEGORIES = [
              { nombre: "Limpieza", activo: true, orden: 1 },
              { nombre: "Música", activo: true, orden: 2 },
              { nombre: "Atención", activo: true, orden: 3 },
              { nombre: "Ambiente", activo: true, orden: 4 },
              { nombre: "Servicio", activo: true, orden: 5 },
            ];

            console.log("Evento recibido:", responseData.evento);
            console.log(
              "Categorías del evento:",
              responseData.evento.categoriasRatings,
            );

            if (
              responseData.evento.categoriasRatings &&
              Array.isArray(responseData.evento.categoriasRatings) &&
              responseData.evento.categoriasRatings.length > 0
            ) {
              const activas = responseData.evento.categoriasRatings
                .filter((cat) => {
                  const esValida =
                    cat &&
                    cat.activo !== false &&
                    cat.nombre &&
                    cat.nombre !== "undefined" &&
                    cat.nombre !== "null" &&
                    cat.nombre.trim() !== "";
                  if (!esValida) {
                    console.warn(
                      "⚠️ Categoría filtrada (inválida o inactiva):",
                      cat,
                    );
                  }
                  return esValida;
                })
                .sort((a, b) => (a.orden || 0) - (b.orden || 0));
              console.log(
                "✅ Usando categorías personalizadas (activas y válidas):",
                activas,
              );
              console.log("📊 Total categorías:", activas.length);
              setCategorias(activas);
            } else {
              console.log("Usando categorías por defecto");
              setCategorias(DEFAULT_CATEGORIES);
            }

            const businessData = responseData.business;
            setBusinessData(businessData);
            const tipedsRes = await cargarNegocio(businessData);
            setBusiness(businessData);
            if (tipedsRes.status === 200) {
              setTipeds(
                sortTipedsForDestino(
                  filterTipedsForNuclio(tipedsRes.data, null),
                ),
              );
            }
            setActiveUnidades([]);
            const ratingsActiveValue =
              businessData.ratingsActive !== undefined &&
              businessData.ratingsActive !== null
                ? businessData.ratingsActive === true ||
                  businessData.ratingsActive === "true"
                : true;
            setRatingsActive(ratingsActiveValue);
            console.log(
              "📊 ratingsActive:",
              businessData.ratingsActive,
              "→",
              ratingsActiveValue,
            );
          } else {
            setIsFromEvent(false);
            setIsFromBusiness(true);
            setIsFromTiped(false);
            const businessData = responseData.business || responseData;
            const nuclioData = responseData.nuclioNegocio || null;
            setNuclioNegocio(
              nuclioData
                ? { ...nuclioData, ratingOpcional: !!nuclioData.ratingOpcional }
                : null,
            );
            setBusinessData(businessData);

            const tipedsRes = await cargarNegocio(businessData);
            setBusiness(businessData);
            const allTipeds =
              tipedsRes.status === 200 ? tipedsRes.data || [] : [];
            const filteredTipeds = filterTipedsForNuclio(allTipeds, nuclioData);
            setTipeds(sortTipedsForDestino(filteredTipeds));
            const cargoIdsWithTipeds = businessData.vives
              ? null
              : new Set(
                  allTipeds
                    .filter((t) => t.active && !t.deleted)
                    .map((t) => String(t.cargo?._id || t.cargo)),
                );
            const nuclioUnidades = getActiveUnidades(
              nuclioData,
              cargoIdsWithTipeds,
            );
            setActiveUnidades(sortUnidadesForDestino(nuclioUnidades));
            const ratingsActiveValue =
              businessData.ratingsActive !== undefined &&
              businessData.ratingsActive !== null
                ? businessData.ratingsActive === true ||
                  businessData.ratingsActive === "true"
                : true;
            setRatingsActive(ratingsActiveValue);
          }
        }

        if (!responseData) {
          try {
            if (tipedRes?.status === 410) {
              setNegocioInhabilitado(true);
              setIsLoading(false);
              return;
            }
            if (tipedRes?.status === 200) {
              setSelectedTiped(tipedRes.data);
              setHasSelectedDestino(true);

              setIsFromBusiness(false);
              setIsFromTiped(true);
              const tipedData = tipedRes.data;
              const businessFromTiped = await getBusinessById(
                tipedData.business,
              );
              if (businessFromTiped.status === 200) {
                const businessData = businessFromTiped.data;
                await cargarNegocio(businessData, { conTipeds: false });
                setTiped(tipedData);
                setBusiness(businessData);
                setRatingsActive(false);
              }
            } else {
              throw new Error(
                "No se pudo obtener el negocio, tiped ni evento.",
              );
            }
          } catch (tipedError) {
            console.log("No es un QR de tiped:", tipedError);
            throw new Error("No se pudo obtener el negocio, tiped ni evento.");
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [recipientId]);

  // Actualizar textos cuando cambie el idioma
  useEffect(() => {
    if (textCookiesContent) {
      setTextCoookies(getTextByLanguage(textCookiesContent));
    }
    if (textLegalContent) {
      setTextLegal(getTextByLanguage(textLegalContent));
    }
    // Forzar re-render de categorías cuando cambie el idioma para actualizar traducciones
    if (categorias.length > 0) {
      setCategorias([...categorias]);
    }
  }, [i18n.language, textCookiesContent, textLegalContent]);

  // Limpiar errores inline cuando el usuario completa cada campo
  useEffect(() => {
    setFormErrors((prev) => ({
      amount: selectedTip ? false : prev.amount,
      destino: hasSelectedDestino ? false : prev.destino,
      rating: rating ? false : prev.rating,
    }));
  }, [selectedTip, hasSelectedDestino, rating]);

  // Si el QR solo tiene «A todos», ocultar selector y fijar destino por defecto
  useEffect(() => {
    if (!isFromBusiness || !nuclioNegocio) return;
    const chips = buildVisibleDestinoChips(
      nuclioNegocio,
      activeUnidades,
      tipeds,
    );
    if (!isSoloRepartoColectivo(chips)) return;
    setSelectedRepartoColectivo(true);
    setSelectedUnidadTrabajo(null);
    setSelectedTiped(null);
    setHasSelectedDestino(true);
  }, [isFromBusiness, nuclioNegocio, activeUnidades, tipeds]);

  // Función para guardar automáticamente el rating de evento (sin navegar)
  const handleAutoSaveEventRating = async (selectedRating) => {
    if (!isFromEvent || !eventData?.soloRatings || savingRating) return;

    setSavingRating(true);
    try {
      setLandOut(new Date());
      let tip = {
        rating: selectedRating,
        comentario: comentario,
        evento: eventData._id,
        qrCodigo: qrCodigo,
        zona: zona,
        business: business._id,
        landIn: landIn,
        landOut: new Date(),
        estadoValorado: "valorado",
        ratingsDetallados:
          Object.keys(ratingsDetallados).length > 0
            ? ratingsDetallados
            : undefined,
      };

      tip.tiper = await ensureTiperId();

      let res;
      if (savedTipId) {
        res = await updateTip(savedTipId, tip);
      } else {
        // Crear nuevo tip
        res = await postTip(tip);
        if (res.status === 201 || res.status === 200) {
          setSavedTipId(res.data.id);
        }
      }
    } catch (error) {
      console.error("Error al guardar rating:", error);
      Swal.fire({
        icon: "error",
        title: t("common.oops"),
        text: t("formErrors.saveRating"),
      });
    } finally {
      setSavingRating(false);
    }
  };

  // Función para guardar rating detallado de categoría
  // Función helper para obtener el texto a mostrar de una categoría según el idioma
  const getCategoriaDisplayText = (categoria) => {
    if (!categoria) return "";

    // Obtener idioma actual (normalizado, ej: "es", "en")
    const currentLang = i18n.language?.split("-")[0] || "es";

    // Si la categoría tiene traducciones
    if (categoria.traducciones && typeof categoria.traducciones === "object") {
      const traduccion = categoria.traducciones[currentLang];

      if (traduccion) {
        // Si es tipo "pregunta" y existe pregunta, mostrar pregunta
        if (
          categoria.tipo === "pregunta" &&
          traduccion.pregunta &&
          traduccion.pregunta.trim()
        ) {
          return traduccion.pregunta;
        }
        // Si existe nombre traducido, mostrarlo
        if (traduccion.nombre && traduccion.nombre.trim()) {
          return traduccion.nombre;
        }
      }
    }

    // Fallback: si es predefinida pero no tiene traducción en este idioma, buscar en otros idiomas
    if (categoria.esPredefinida && categoria.traducciones) {
      // Intentar español primero
      if (categoria.traducciones.es) {
        if (
          categoria.tipo === "pregunta" &&
          categoria.traducciones.es.pregunta &&
          categoria.traducciones.es.pregunta.trim()
        ) {
          return categoria.traducciones.es.pregunta;
        }
        if (
          categoria.traducciones.es.nombre &&
          categoria.traducciones.es.nombre.trim()
        ) {
          return categoria.traducciones.es.nombre;
        }
      }
      // Si no, buscar en cualquier idioma disponible
      for (const langCode in categoria.traducciones) {
        const trad = categoria.traducciones[langCode];
        if (trad) {
          if (
            categoria.tipo === "pregunta" &&
            trad.pregunta &&
            trad.pregunta.trim()
          ) {
            return trad.pregunta;
          }
          if (trad.nombre && trad.nombre.trim()) {
            return trad.nombre;
          }
        }
      }
    }

    // Fallback final: usar el nombre clave (compatibilidad con categorías antiguas)
    return categoria.nombre || "";
  };

  const handleCategoryRating = async (categoriaNombre, ratingValue) => {
    if (!isFromEvent || !eventData?.soloRatings) return;

    // Validar que el nombre de la categoría sea válido
    if (
      !categoriaNombre ||
      categoriaNombre === "undefined" ||
      categoriaNombre === "null" ||
      categoriaNombre.trim() === ""
    ) {
      console.error(
        "⚠️ Intento de guardar rating con nombre de categoría inválido:",
        categoriaNombre,
      );
      return;
    }

    const newRatingsDetallados = {
      ...ratingsDetallados,
      [categoriaNombre]: ratingValue,
    };
    setRatingsDetallados(newRatingsDetallados);

    // Si ya tenemos un tip guardado, actualizarlo
    if (savedTipId) {
      setSavingRating(true);
      try {
        const tiperId = await ensureTiperId();
        const updatedTip = {
          rating: rating,
          comentario: comentario,
          evento: eventData._id,
          qrCodigo: qrCodigo,
          zona: zona,
          landIn,
          landOut,
          business: business._id,
          ratingsDetallados: newRatingsDetallados,
          tiper: tiperId,
        };
        await updateTip(savedTipId, updatedTip);
      } catch (error) {
        console.error("Error guardando rating detallado:", error);
        // Revertir cambio en caso de error
        setRatingsDetallados(ratingsDetallados);
      } finally {
        setSavingRating(false);
      }
    } else if (rating) {
      // Si no hay tip guardado pero hay rating global, guardar todo
      await handleAutoSaveEventRating(rating);
    }
  };

  const openLegalModal = (title, content, { plainText = false } = {}) => {
    Swal.fire({
      title,
      ...(plainText
        ? { text: content }
        : {
            html: `
        <div style="text-align: left; max-height: 50vh; overflow-y: auto;">
          ${content}
        </div>
      `,
          }),
      width: 800,
      showConfirmButton: true,
      confirmButtonText: t("common.close"),
      confirmButtonColor: "#66bc5f",
    });
  };

  const scrollToFirstFormError = (errors) => {
    const targets = [
      [errors.rating, ratingSectionRef],
      [errors.amount, amountSectionRef],
      [errors.destino, destinoSectionRef],
    ];
    for (const [hasError, ref] of targets) {
      if (hasError && ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        break;
      }
    }
  };

  // Función para finalizar y navegar a success (solo eventos)
  const handleFinishEvent = async () => {
    if (!rating) {
      setFormErrors((prev) => ({ ...prev, rating: true }));
      scrollToFirstFormError({ rating: true, amount: false, destino: false });
      return;
    }

    if (!savedTipId && rating) {
      await handleAutoSaveEventRating(rating);
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    if (savedTipId) {
      try {
        const tiperId = await ensureTiperId();
        await updateTip(savedTipId, { tiper: tiperId });
      } catch (error) {
        console.error("Error al vincular tiper antes de finalizar:", error);
        Swal.fire({
          icon: "error",
          title: t("common.oops"),
          text: t("formErrors.saveRating"),
        });
        return;
      }
      navigate(`/success?tipId=${savedTipId}`);
    } else {
      setFormErrors((prev) => ({ ...prev, rating: true }));
      scrollToFirstFormError({ rating: true, amount: false, destino: false });
    }
  };

  const handleFixedTipSelection = (tip) => {
    setIsCustomTipSelected(false);
    setCustomTipAmount("");
    setCustomTipModalOpen(false);
    event({
      action: "select_tip_amount",
      category: "Donaciones",
      label: "Pantalla Inicial",
      value: tip.import,
    });
    if (!importeElegidoAtRef.current) importeElegidoAtRef.current = new Date();
    embudo.registrar("importe_elegido");
    setSelectedTip(tip);
  };

  const handleCustomTipSelection = () => {
    setCustomTipDraft(customTipAmount || "");
    setCustomTipInputFocused(false);
    setCustomTipModalOpen(true);
  };

  const handleCustomTipModalConfirm = () => {
    const sanitized = parseCustomTipInput(customTipDraft);
    if (!isValidCustomTipAmount(sanitized)) {
      return;
    }
    const amount = roundCustomTipAmount(sanitized);
    setCustomTipAmount(sanitized);
    if (!importeElegidoAtRef.current) importeElegidoAtRef.current = new Date();
    embudo.registrar("importe_elegido");
    setSelectedTip({ id: CUSTOM_TIP_ID, import: amount });
    setIsCustomTipSelected(true);
    setCustomTipModalOpen(false);
    setCustomTipInputFocused(false);
    event({
      action: "select_tip_amount",
      category: "Donaciones",
      label: "Pantalla Inicial",
      value: amount,
    });
  };

  const handleCustomTipModalCancel = () => {
    setCustomTipModalOpen(false);
    setCustomTipInputFocused(false);
    setCustomTipDraft("");
    setCustomTipAmount("");
    setIsCustomTipSelected(false);
    if (selectedTip?.id === CUSTOM_TIP_ID) {
      setSelectedTip(null);
    }
  };

  const handleCustomTipDraftChange = (event) => {
    setCustomTipDraft(parseCustomTipInput(event.target.value));
  };

  useEffect(() => {
    if (!customTipModalOpen) {
      setCustomTipInputFocused(false);
      return undefined;
    }

    const focusInput = () => {
      const input = customTipInputRef.current;
      if (!input) return;
      input.focus({ preventScroll: true });
    };

    // MUI Dialog atrapa el foco al abrir; reintentamos tras el mount/animación
    const t0 = window.setTimeout(focusInput, 0);
    const t1 = window.setTimeout(focusInput, 80);
    return () => {
      window.clearTimeout(t0);
      window.clearTimeout(t1);
    };
  }, [customTipModalOpen]);

  // Embudo propio: la visita cuenta cuando sabemos el negocio y por dónde entró
  useEffect(() => {
    if (!business?._id) return;
    const canal = isFromEvent ? "evento" : isFromTiped ? "qr_empleado" : "qr_negocio";
    embudo.paginaCargada({ negocio: business._id, canal });
  }, [business?._id, isFromEvent, isFromTiped]);
  useEffect(() => embudo.vigilarAbandono(), []);

  /** Validación del formulario (mismas reglas para "Continuar" y el pago exprés). */
  const getFormValidation = () => {
    const isEventOnlyRatings = isFromEvent && eventData?.soloRatings;
    const ratingOpcional = !!nuclioNegocio?.ratingOpcional;
    const ratingRequired =
      isFromEvent || (ratingsActive && isFromBusiness && !ratingOpcional);
    const visibleChips = isFromBusiness
      ? buildVisibleDestinoChips(nuclioNegocio, activeUnidades, tipeds)
      : [];
    const needsDestinoSelection =
      isFromBusiness && shouldShowChipSelector(visibleChips);

    const errors = {
      amount: !isEventOnlyRatings && !selectedTip,
      destino:
        !isEventOnlyRatings && needsDestinoSelection && !hasSelectedDestino,
      rating: ratingRequired && !rating,
    };

    return {
      errors,
      isValid: !(errors.amount || errors.destino || errors.rating),
      isEventOnlyRatings,
      visibleChips,
    };
  };

  /** Marca los errores del formulario y lleva al primero. */
  const showFormErrors = (errors) => {
    setFormErrors(errors);
    scrollToFirstFormError(errors);
  };

  /**
   * Crea (o actualiza, si ya existe) la propina con los datos del formulario.
   * Devuelve el id de la propina; lanza si no se puede guardar.
   */
  const persistTip = async ({ isEventOnlyRatings, visibleChips }) => {
    if (!isEventOnlyRatings) embudo.registrar("pago_iniciado");
    let tip = {};
    // Solo incluir importe si no es evento solo de ratings
    if (!isEventOnlyRatings && selectedTip) {
      tip.importe = selectedTip;
      // Captura analítica: opciones mostradas, cuál eligió, idioma y móvil
      tip.captura = construirCaptura({
        opciones: tips,
        libre: isCustomTipSelected || selectedTip.id === CUSTOM_TIP_ID,
        idioma: i18n.language,
        opcionesVistasAt: landIn,
        importeElegidoAt: importeElegidoAtRef.current,
        sesionEmbudo: embudo.sesionActual(),
      });
    }
    tip.comentario = comentario;
    // Campana de propinas: viaja con la propina antes de cualquier forma de pago
    if (!isEventOnlyRatings && isFromBusiness && business?.campanaActiva) {
      tip.campana = campana;
    }
    // Solo incluir rating si es un business y ratingsActive está activo
    if (isFromBusiness && ratingsActive && rating) {
      tip.rating = rating;
    }
    // Si es evento, agregar campos del evento
    if (isFromEvent && eventData) {
      tip.evento = eventData._id;
      tip.qrCodigo = qrCodigo;
      tip.zona = zona;
      // Para eventos, el rating es obligatorio
      if (rating) {
        tip.rating = rating;
      }
      // Agregar ratings detallados si hay alguno
      if (Object.keys(ratingsDetallados).length > 0) {
        tip.ratingsDetallados = ratingsDetallados;
      }
    }
    tip.business = business._id;
    if (nuclioNegocio?._id) {
      tip.nuclioNegocio = nuclioNegocio._id;
    }
    tip.landIn = landIn;
    // Solo incluir términos y tasas si no es evento solo de ratings
    if (!isEventOnlyRatings) {
      tip.clickTerms = true; // Aceptación implícita al continuar
      tip.clickTaxes = payFees;
      tip.tasa = tasa;
    }
    tip.tiped = selectedTiped?._id || null;
    tip.unidadTrabajo = selectedUnidadTrabajo || null;
    tip.repartoColectivo =
      isSoloRepartoColectivo(visibleChips) || !!selectedRepartoColectivo;

    tip.tiper = await ensureTiperId();

    let tipId = savedTipId;
    if (savedTipId) {
      await updateTip(savedTipId, tip);
    } else {
      const res = await postTip(tip);
      tipId = res.data.id;
      setSavedTipId(tipId);
    }

    // Guardar estado antes de navegar (solo si no es evento solo de ratings)
    if (!isEventOnlyRatings) {
      saveFormStateToSession({
        rating,
        comentario,
        selectedTiped,
        selectedUnidadTrabajo,
        selectedRepartoColectivo,
        hasSelectedDestino,
        selectedTip,
        isCustomTipSelected,
        customTipAmount,
        payFees,
        campana,
        savedTipId: tipId,
        ratingsDetallados,
        recipientId,
      });
    }

    // Tracking: propina/rating guardado exitosamente
    event({
      action: "propina_rating_guardado",
      category: "Formulario",
      label: isEventOnlyRatings ? "Rating Evento" : "Propina",
      value: tip?.importe?.import || 0,
    });

    return tipId;
  };

  /**
   * @param {{ viaTpv?: boolean, ubicacion?: string | null }} options viaTpv:
   * "Pagar con el datáfono del camarero" -> tras guardar la propina se avisa a
   * la app TPV del local; ubicacion: dónde está el cliente (opcional).
   */
  const handleSubmit = async (options = {}) => {
    const { errors, isValid, isEventOnlyRatings, visibleChips } =
      getFormValidation();

    if (!isValid) {
      showFormErrors(errors);
      return;
    }

    setFormErrors({ amount: false, destino: false, rating: false });

    let tipId;
    try {
      tipId = await persistTip({ isEventOnlyRatings, visibleChips });
    } catch (error) {
      console.error("Error al guardar la propina:", error);
      Swal.fire({
        icon: "error",
        title: t("common.oops"),
        text: t("formErrors.sendRating"),
      });
      return;
    }

    // Para eventos solo de ratings, ir directamente a success
    if (isEventOnlyRatings) {
      clearFormStateFromSession(); // Limpiar estado al completar
      navigate(`/success?tipId=${tipId}`);
    } else {
      const paymentMethod = options.viaTpv
        ? "tpv"
        : business.paymentProvider || "redsys";
      // La ubicación va en el state (no en la URL): la lee TpvWaitScreen
      const navState =
        options.viaTpv && options.ubicacion
          ? { state: { tpvUbicacion: options.ubicacion } }
          : undefined;
      navigate(`payout/${tipId}?method=${paymentMethod}`, navState);
    }
  };

  // ---- Pago exprés con Apple Pay / Google Pay (solo negocios Stripe) ----
  // Fase 1: solo con ?express=1 (con panel de diagnóstico). Fase 2:
  // REACT_APP_EXPRESS_WALLET=on lo activa por defecto (?express=0 lo quita).
  const isEventOnlyRatingsView = isFromEvent && eventData?.soloRatings;
  const expressEnabled =
    expressMode !== "off" &&
    !isLoading &&
    business?.paymentProvider === "stripe" &&
    !isEventOnlyRatingsView;
  const expressAmountCents = (() => {
    const importe = Number(selectedTip?.import || 0);
    if (!importe) return 0;
    const total = payFees ? importe + calcularTasaFinal(importe, tasa) : importe;
    const cents = Math.round(total * 100);
    return Number.isFinite(cents) ? cents : 0;
  })();
  const expressValidation = expressEnabled ? getFormValidation() : null;

  const handleExpressSaveTip = async () => {
    const { errors, isValid, isEventOnlyRatings, visibleChips } =
      getFormValidation();
    if (!isValid) {
      showFormErrors(errors);
      throw new Error("Formulario incompleto");
    }
    return persistTip({ isEventOnlyRatings, visibleChips });
  };

  const handleExpressPaid = (tipId) => {
    event({
      action: "pago_express_ok",
      category: "Pago",
      label: "Pantalla Inicial",
    });
    clearFormStateFromSession();
    navigate(`/${recipientId}/success/${tipId}?method=stripe`);
  };

  // ---- Pago en un toque con la tarjeta guardada (tiper identificado) ----
  // Mismo interruptor que el exprés (?express=0 lo apaga). Sin tarjeta
  // guardada, la pantalla queda exactamente como antes.
  const oneTapCard =
    expressEnabled && paymentProfile?.canOneTap
      ? paymentProfile.paymentMethods?.[0] || null
      : null;
  const showOneTap = !!oneTapCard && oneTapState !== "failed";
  const oneTapProcessing = oneTapState === "processing";

  const failOneTap = (message) => {
    oneTapBusyRef.current = false;
    setOneTapState("failed");
    setOneTapMessage(message || t("oneTap.failed"));
  };

  const handleOneTapPay = async () => {
    if (!oneTapCard || oneTapBusyRef.current || walletProcessing) return;
    const { errors, isValid, isEventOnlyRatings, visibleChips } =
      getFormValidation();
    if (!isValid) {
      showFormErrors(errors);
      return;
    }
    // Bloqueo síncrono: un doble toque no puede lanzar dos cobros
    oneTapBusyRef.current = true;
    setOneTapState("processing");
    setOneTapMessage(null);
    setFormErrors({ amount: false, destino: false, rating: false });
    event({
      action: "click_pago_un_toque",
      category: "Pago",
      label: "Pantalla Inicial",
    });

    let tipId;
    try {
      tipId = await persistTip({ isEventOnlyRatings, visibleChips });
    } catch (error) {
      console.error("Error al guardar la propina:", error);
      oneTapBusyRef.current = false;
      setOneTapState("idle");
      Swal.fire({
        icon: "error",
        title: t("common.oops"),
        text: t("formErrors.sendRating"),
      });
      return;
    }

    try {
      const res = await postOneTapPayment({
        tipId,
        paymentMethodId: oneTapCard.id,
      });
      const data = res?.data || {};
      let finalStatus = data.status;
      if (finalStatus === "requires_action" && data.clientSecret) {
        // 3DS: Stripe abre el reto del banco y confirma el mismo PaymentIntent
        const stripe = await getStripePromise();
        if (!stripe) throw new Error("stripe.js no disponible");
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          data.clientSecret,
          { payment_method: data.paymentMethodId || oneTapCard.id },
        );
        if (error) {
          console.warn("3DS del pago en un toque no completado:", error.message);
          failOneTap();
          return;
        }
        finalStatus = paymentIntent?.status;
      }
      if (finalStatus === "succeeded" || finalStatus === "processing") {
        event({
          action: "pago_un_toque_ok",
          category: "Pago",
          label: "Pantalla Inicial",
        });
        clearFormStateFromSession();
        navigate(`/${recipientId}/success/${tipId}?method=stripe`);
        return;
      }
      failOneTap();
    } catch (err) {
      console.warn(
        "Pago en un toque no completado:",
        err?.response?.data || err?.message,
      );
      failOneTap(isTipAlreadyPaidError(err) ? t("express.alreadyPaid") : null);
    }
  };

  // Separador decimal del idioma de la pantalla (2,00 € en es/ca/fr; 2.00 en en)
  const oneTapAmountLabel = (expressAmountCents / 100).toLocaleString(
    i18n?.language || "es-ES",
    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
  );

  const oneTapButton = showOneTap ? (
    <Button
      fullWidth
      variant="contained"
      disabled={oneTapProcessing || walletProcessing}
      style={{
        backgroundColor: "#66bc5f",
        fontSize: "16px",
        color: "white",
        borderRadius: "30px",
        padding: "9px 14px",
        // Algo más ancho que "Continuar": marca, últimos 4 e importe en una línea
        width: "310px",
        maxWidth: "100%",
        lineHeight: 1.25,
        textTransform: "none",
        border: "none",
        marginBottom: "10px",
        opacity: oneTapProcessing || walletProcessing ? 0.7 : 1,
      }}
      onClick={handleOneTapPay}
    >
      {oneTapProcessing
        ? t("oneTap.processing")
        : expressAmountCents > 0
          ? t("oneTap.payWithCard", {
              brand: cardBrandLabel(oneTapCard.brand),
              last4: oneTapCard.last4,
              amount: oneTapAmountLabel,
            })
          : t("oneTap.payWithCardNoAmount", "Pagar con tu {{brand}} ···· {{last4}}", {
              brand: cardBrandLabel(oneTapCard.brand),
              last4: oneTapCard.last4,
            })}
    </Button>
  ) : null;

  // Botón "Continuar" de siempre (también el fallback del pago exprés)
  const continueButton = (
    <Button
      fullWidth
      variant="contained"
      style={{
        backgroundColor: "#66bc5f",
        fontSize: "17px",
        color: "white",
        borderRadius: "30px",
        padding: "8px",
        width: "260px",
        textTransform: "none",
        border: "none",
      }}
      onClick={() => {
        event({
          action: "click_continuar",
          category: "Formulario",
          label: "Pantalla Inicial",
        });
        handleSubmit();
      }}
    >
      {t("common.continue")}
    </Button>
  );

  // Construir nombres para SEO
  const businessName = business?.nombre_comercial || "";
  const tipedName =
    isFromTiped && tiped?.nombre
      ? `${tiped.nombre} ${tiped.apellidos || ""}${
          tiped.alias ? ` (${tiped.alias})` : ""
        }`.trim()
      : "";

  // Negocio inhabilitado: mensaje sin formulario
  if (negocioInhabilitado) {
    return (
      <Container
        className="app-container"
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div className="d-flex justify-content-center mb-0">
          <img src={logoSmall} alt="Logo" className="logo" style={{ maxHeight: "80px", objectFit: "contain" }} />
        </div>
        <div style={{ textAlign: "center", maxWidth: "520px" }}>
          <h2 style={{ color: "#333", marginBottom: "16px", fontSize: "26px" }}>{t("businessDisabled.title")}</h2>
          <p style={{ color: "#666", fontSize: "17px", lineHeight: "1.6" }}>{t("businessDisabled.description")}</p>
        </div>
      </Container>
    );
  }

  // Mostrar mensaje cuando no hay recipientId
  if (noRecipientId) {
    return (
      <Container
        className="app-container"
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <div className="d-flex justify-content-center mb-0">
          <img
            src={logoSmall}
            alt="Logo"
            className="logo"
            style={{ maxHeight: "80px", objectFit: "contain" }}
          />
        </div>
        <div style={{ textAlign: "center", maxWidth: "600px" }}>
          <h2 style={{ color: "#333", marginBottom: "20px", fontSize: "28px" }}>
            {t("welcome.title")}
          </h2>
          <p
            style={{
              color: "#666",
              fontSize: "18px",
              marginBottom: "30px",
              lineHeight: "1.6",
            }}
          >
            {t("welcome.description")}
          </p>
          <div
            style={{
              backgroundColor: "#f0f8f0",
              padding: "20px",
              borderRadius: "12px",
              marginBottom: "30px",
            }}
          >
            <p style={{ color: "#555", fontSize: "16px", margin: 0 }}>
              <strong>💡 {t("welcome.howItWorks")}</strong>
            </p>
            <ul
              style={{
                textAlign: "left",
                color: "#666",
                fontSize: "15px",
                marginTop: "15px",
                paddingLeft: "20px",
              }}
            >
              <li style={{ marginBottom: "10px" }}>{t("welcome.step1")}</li>
              <li style={{ marginBottom: "10px" }}>{t("welcome.step2")}</li>
              <li style={{ marginBottom: "10px" }}>{t("welcome.step3")}</li>
            </ul>
          </div>
          <p style={{ color: "#888", fontSize: "14px", marginTop: "30px" }}>
            {t("welcome.businessLink")}{" "}
            <a
              href="https://tipify.tech"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#66bc5f", textDecoration: "none" }}
            >
              {t("welcome.visitWebsite")}
            </a>
          </p>
        </div>

        <PoweredByTipify />
      </Container>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          zIndex: 9999,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          style={{
            width: "80px",
            height: "80px",
            maxWidth: "80px",
            maxHeight: "80px",
            color: "#66bc5f",
          }}
        />
      </div>
    );
  }

  const isOnFreePlan =
    business?.planSuscripcionFree === true ||
    businessData?.planSuscripcionFree === true;
  const hasVives = business?.vives === true || businessData?.vives === true;
  const suppressBusinessLogoForPlan = isOnFreePlan && !hasVives;

  const businessLogoForHeader = suppressBusinessLogoForPlan
    ? null
    : resolveStoredMediaUrl(business?.logoURL || businessData?.logoURL);

  const repartoColectivoIconUrl = resolveStoredMediaUrl(
    business?.repartoColectivoIconURL || businessData?.repartoColectivoIconURL,
  );
  const repartoColectivoIconSrc =
    repartoColectivoIconUrl || businessLogoForHeader || logoSmall;
  const repartoColectivoLabelText =
    getTextByLanguage(
      business?.repartoColectivoLabel || businessData?.repartoColectivoLabel,
    ) || t("form.opcion.todos");

  const estilosMarca =
    business?.estilosMarca || businessData?.estilosMarca || {};
  const avatarFondoColor = estilosMarca.colorFondoAvatar || "#e8f5e9";
  const avatarSiglasColor = estilosMarca.colorSiglasAvatar || "#66bc5f";
  const tipedAvatarStyle = {
    background: avatarFondoColor,
  };
  const tipedAvatarInitialsStyle = {
    color: avatarSiglasColor,
  };
  const tipedDirectAvatarStyle = {
    background: avatarFondoColor,
    borderColor: avatarSiglasColor,
  };

  return (
    <>
      <SEO
        businessName={businessName}
        tipedName={tipedName}
        isBusiness={isFromBusiness}
        isTiped={isFromTiped}
        url={window.location.href}
      />
      <Container
        className="app-container"
        style={{ position: "relative", minHeight: "100vh", paddingTop: "4px" }}
      >
        {isFromEvent ? (
          <>
            <EventHeader
              eventData={eventData}
              businessData={businessData}
              showUbicacion={false}
              showLanguageSelector={false}
              showWifi={false}
            />
            <div className="form-header-meta form-header-meta--event">
              <div className="form-header-business-name">
                <FmdGoodOutlinedIcon
                  className="me-1"
                  sx={{ fontSize: 16, verticalAlign: "text-bottom" }}
                />
                <strong>
                  {eventData?.espacio?.nombre || business.nombre_comercial}
                </strong>
              </div>
              <div className="form-header-address text-secondary adress">
                {(() => {
                  if (eventData?.espacio?.direccion) {
                    const espacioDir = eventData.espacio.direccion;
                    const partes = [];
                    if (espacioDir.direccion) partes.push(espacioDir.direccion);
                    if (espacioDir.codigoPostal)
                      partes.push(espacioDir.codigoPostal);
                    if (espacioDir.poblacion) {
                      const poblacion =
                        typeof espacioDir.poblacion === "object"
                          ? espacioDir.poblacion.fullName ||
                            espacioDir.poblacion.nombre ||
                            ""
                          : espacioDir.poblacion;
                      if (poblacion) partes.push(poblacion);
                    }
                    if (partes.length > 0) return partes.join(", ");
                  }
                  return `${business.direccion}, ${business.codigo_postal} ${business.localidad}`;
                })()}
              </div>
              <div className="form-header-meta-row">
                <div className="form-header-rating">
                  <span>{t("common.rating")}</span>
                  <StarIcon sx={{ color: "#ffb304", fontSize: "15px" }} />
                  <span className="text-secondary">
                    {eventData?.promedioRating !== undefined
                      ? eventData.promedioRating.toFixed(1)
                      : ratingValue}
                  </span>
                </div>
                <LanguageSelector />
              </div>
            </div>
          </>
        ) : (
          <>
            {isFromBusiness &&
              !isFromTiped &&
              businessData?.contenidoIntermedio?.activo && (
                <BusinessViewSwitcher
                  tipo={businessData.contenidoIntermedio.tipo}
                  active="tips"
                  onSelectContent={() =>
                    navigate(`/business/${recipientId}?view=menu`)
                  }
                />
              )}
            <div className="form-header-compact">
              <div className="form-header-logo">
                <img
                  src={businessLogoForHeader || logoSmall}
                  alt={
                    businessLogoForHeader
                      ? business.nombre_comercial ||
                        businessData?.nombre_comercial ||
                        "Logo"
                      : "Tipify"
                  }
                />
              </div>
              <div className="form-header-meta">
                {isFromTiped && tiped?.nombre && (
                  <div className="form-header-tiped-name">
                    {tiped.nombre}
                    {tiped.alias ? ` (${tiped.alias})` : ""}
                  </div>
                )}
                <div className="form-header-business-name">
                  <FmdGoodOutlinedIcon
                    className="me-1"
                    sx={{ fontSize: 16, verticalAlign: "text-bottom" }}
                  />
                  <strong>{business.nombre_comercial}</strong>
                </div>
                <div className="form-header-address text-secondary adress">
                  {`${business.direccion}, ${business.codigo_postal} ${business.localidad}`}
                </div>
                <div className="form-header-meta-row">
                  <div className="form-header-rating">
                    <span>{t("common.rating")}</span>
                    <StarIcon sx={{ color: "#ffb304", fontSize: "15px" }} />
                    <span className="text-secondary">{ratingValue}</span>
                  </div>
                  <LanguageSelector />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Estrellas - Solo para businesses, no para tipeds */}
        {((ratingsActive && isFromBusiness) || (isFromEvent && eventData)) && (
          <>
            <div
              ref={ratingSectionRef}
              className={`d-flex flex-column align-items-center form-rating-stars ${
                formErrors.rating ? "form-section-error" : ""
              }`}
            >
              <div className="d-flex justify-content-center">
                <div className="d-flex align-items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) =>
                    star <= (rating || 0) ? (
                      <StarIcon
                        key={star}
                        sx={{
                          color: "#ffb304",
                          fontSize: "36px",
                          cursor: savingRating ? "wait" : "pointer",
                          opacity: savingRating ? 0.6 : 1,
                        }}
                        onClick={() => {
                          if (savingRating) return;
                          event({
                            action: "click_star",
                            category: "Valoraciones",
                            label: "Pantalla Inicial",
                            value: star,
                          });
                          setRating(star);
                          // Si es evento solo de ratings, guardar automáticamente
                          if (isFromEvent && eventData?.soloRatings) {
                            handleAutoSaveEventRating(star);
                          }
                        }}
                      />
                    ) : (
                      <StarBorderIcon
                        key={star}
                        sx={{
                          color: "#a0a4c1",
                          fontSize: "36px",
                          cursor: savingRating ? "wait" : "pointer",
                          opacity: savingRating ? 0.6 : 1,
                        }}
                        onClick={() => {
                          if (savingRating) return;
                          event({
                            action: "click_star",
                            category: "Valoraciones",
                            label: "Pantalla Inicial",
                            value: star,
                          });
                          setRating(star);
                          // Si es evento solo de ratings, guardar automáticamente
                          if (isFromEvent && eventData?.soloRatings) {
                            handleAutoSaveEventRating(star);
                          }
                        }}
                      />
                    ),
                  )}
                </div>
              </div>
              <div className="form-field-error-slot" aria-live="polite">
                {formErrors.rating ? (
                  <p className="form-field-error" role="alert">
                    {isFromEvent
                      ? t("form.validation.eventRatingRequired")
                      : t("form.validation.ratingRequired")}
                  </p>
                ) : null}
              </div>
            </div>

            <div className="d-flex flex-column align-items-center form-comment-block">
              <strong className="mb-1" style={{ fontSize: "14px" }}>
                {isFromBusiness && !!nuclioNegocio?.ratingOpcional
                  ? t("form.rating.titleOptional")
                  : t("form.rating.title")}
              </strong>
              <TextField
                fullWidth
                multiline
                rows={1}
                variant="outlined"
                size="small"
                onChange={(e) => setComentario(e.target.value)}
                onBlur={(e) => {
                  if (e.target.value.trim() !== "") {
                    event({
                      action: "escribir_comentario",
                      category: "Opiniones",
                      label: "Pantalla Inicial",
                      value: e.target.value.length,
                    });
                  }
                }}
                placeholder={t("form.comment.placeholder")}
              />
            </div>
          </>
        )}

        {/* Categorías de Ratings - Solo para eventos solo de ratings */}
        {isFromEvent && eventData?.soloRatings && categorias.length > 0 && (
          <>
            <div className="d-flex flex-column align-items-center my-4">
              {/* Botón para expandir/colapsar categorías */}
              <div
                className="w-100 d-flex align-items-center justify-content-between mb-3"
                style={{
                  maxWidth: "600px",
                  cursor: "pointer",
                  padding: "12px 16px",
                  border: "1px solid #66bc5f",
                  borderRadius: "8px",
                  backgroundColor: "#f9f9f9",
                  transition: "background-color 0.2s",
                }}
                onClick={() => setCategoriasExpanded(!categoriasExpanded)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f0f0f0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <strong style={{ fontSize: "16px" }}>
                    {t("form.categories.title")}
                  </strong>
                  <span style={{ fontSize: "14px", color: "#666" }}>
                    (
                    {
                      Object.keys(ratingsDetallados).filter(
                        (key) => ratingsDetallados[key] > 0,
                      ).length
                    }
                    /{categorias.length} {t("form.categories.rated")})
                  </span>
                </div>
                {categoriasExpanded ? (
                  <ExpandLessIcon style={{ color: "#666" }} />
                ) : (
                  <ExpandMoreIcon style={{ color: "#666" }} />
                )}
              </div>

              {/* Categorías expandibles */}
              {categoriasExpanded && (
                <div className="w-100" style={{ maxWidth: "600px" }}>
                  {categorias
                    .filter(
                      (categoria) =>
                        categoria &&
                        categoria.nombre &&
                        categoria.nombre !== "undefined",
                    ) // Filtrar categorías inválidas
                    .map((categoria) => {
                      const categoriaRating =
                        ratingsDetallados[categoria.nombre] || 0;
                      const isValorada = categoriaRating > 0;
                      const displayText = getCategoriaDisplayText(categoria);

                      return (
                        <div
                          key={categoria.nombre}
                          className="w-100 mb-3 p-3"
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            backgroundColor: isValorada ? "#f0f8f0" : "#fff",
                            transition: "background-color 0.3s",
                          }}
                        >
                          <div className="d-flex align-items-center justify-content-between mb-2">
                            <div className="d-flex align-items-center gap-2">
                              {isValorada && (
                                <span
                                  style={{ color: "#4caf50", fontSize: "20px" }}
                                >
                                  ✓
                                </span>
                              )}
                              <strong>{displayText}</strong>
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            {[1, 2, 3, 4, 5].map((star) =>
                              star <= categoriaRating ? (
                                <StarIcon
                                  key={star}
                                  sx={{
                                    color: "#ffb304",
                                    fontSize: "32px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    handleCategoryRating(
                                      categoria.nombre,
                                      star,
                                    );
                                  }}
                                />
                              ) : (
                                <StarBorderIcon
                                  key={star}
                                  sx={{
                                    color: "#a0a4c1",
                                    fontSize: "32px",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    handleCategoryRating(
                                      categoria.nombre,
                                      star,
                                    );
                                  }}
                                />
                              ),
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Botón Finalizar para eventos */}
              <div className="d-flex justify-content-center mt-4">
                <Button
                  variant="contained"
                  onClick={handleFinishEvent}
                  disabled={savingRating}
                  style={{
                    backgroundColor: savingRating ? "#dff2de" : "#66bc5f",
                    fontSize: "18px",
                    color: savingRating ? "#888583" : "white",
                    borderRadius: "30px",
                    padding: "10px 40px",
                    textTransform: "none",
                    border: savingRating ? "1px solid #888583" : "none",
                    marginBottom: "20px",
                  }}
                >
                  {savingRating
                    ? t("form.finish.saving")
                    : t("form.finish.button")}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Propina - Ocultar si es evento con soloRatings */}
        {!(isFromEvent && eventData?.soloRatings) && (
          <div
            ref={amountSectionRef}
            className={`d-flex flex-column align-items-center text-center ${
              formErrors.amount ? "form-section-error" : ""
            }`}
          >
            {isFromTiped && tiped && (
              <div className="d-flex flex-column align-items-center mb-1">
                <div
                  className="tiped-direct-avatar"
                  style={tipedDirectAvatarStyle}
                >
                  {tiped.photo ? (
                    <img
                      src={tiped.photo}
                      alt={`${tiped.nombre} ${tiped.apellidos}`}
                    />
                  ) : (
                    <span style={tipedAvatarInitialsStyle}>
                      {`${tiped.nombre?.[0] || ""}${
                        tiped.apellidos?.[0] || ""
                      }`.toUpperCase()}
                    </span>
                  )}
                </div>
                {tiped.personalMessage && (
                  <div className="tiped-direct-message mt-2">
                    "{tiped.personalMessage}"
                  </div>
                )}
                {tiped.instagram && (
                  <a
                    className="tiped-direct-instagram mt-2"
                    href={`https://instagram.com/${encodeURIComponent(tiped.instagram)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: "13px", textDecoration: "none" }}
                  >
                    @{tiped.instagram}
                  </a>
                )}
              </div>
            )}
            <strong className="form-tip-title mb-1" style={{ fontSize: "16px" }}>
              {isFromTiped
                ? t("form.titleTiped", {
                    name: tiped.alias
                      ? `${tiped.nombre} (${tiped.alias})`
                      : tiped.nombre,
                  })
                : t("form.title")}
            </strong>
            <span className="propina px-3">
              {(() => {
                // Lógica condicional para el subtítulo
                if (isFromTiped) {
                  // URL de tiped
                  return t("form.subtitleTiped");
                } else if (isFromBusiness) {
                  const visibleChips = buildVisibleDestinoChips(
                    nuclioNegocio,
                    activeUnidades,
                    tipeds,
                  );
                  const subtitleKey = getFormSubtitleKey(
                    nuclioNegocio,
                    visibleChips,
                  );
                  if (subtitleKey === "form.subtitleBusinessChooseDestino") {
                    const customSubtitle = getTextByLanguage(
                      nuclioNegocio?.subtituloSeleccionDestino,
                    );
                    if (customSubtitle) return customSubtitle;
                  }
                  return t(subtitleKey);
                }
                // Fallback por defecto
                return t("form.subtitle");
              })()}
            </span>
            <div className="d-flex flex-wrap justify-content-center px-3">
              {tips.map((tip, index) => {
                const isLastTip = index === tips.length - 1;
                const propinaEditableActive = isPropinaEditable(
                  businessData || business,
                );
                const isCustomOption = isLastTip && propinaEditableActive;
                const hasConfirmedCustomTip =
                  isCustomOption &&
                  selectedTip?.id === CUSTOM_TIP_ID &&
                  isValidCustomTipAmount(selectedTip.import);
                const isSelected = isCustomOption
                  ? hasConfirmedCustomTip
                  : selectedTip?.id === tip.id;

                return (
                  <div
                    key={isCustomOption ? CUSTOM_TIP_ID : tip.id}
                    className="d-flex justify-content-center opciones"
                    style={{ flexBasis: "33.33%" }}
                  >
                    <Button
                      variant={isSelected ? "contained" : "outlined"}
                      color="primary"
                      aria-label={
                        isCustomOption ? t("form.customTip.label") : undefined
                      }
                      onClick={() => {
                        if (isCustomOption) {
                          handleCustomTipSelection();
                        } else {
                          handleFixedTipSelection(tip);
                        }
                      }}
                      style={{
                        backgroundColor: isSelected ? "#66bc5f" : "#ffffff",
                        fontSize: "20px",
                        fontWeight: isSelected ? "bold" : "normal",
                        color: isSelected ? "#ffffff" : "#333333",
                        borderRadius: "10px",
                        border: isSelected
                          ? "1px solid #66bc5f"
                          : "1px solid #cfcfcf",
                        boxShadow: isSelected
                          ? "0 2px 8px rgba(102, 188, 95, 0.35)"
                          : "0 2px 6px rgba(0, 0, 0, 0.12)",
                        height: "46px",
                        width: "72px",
                        padding: "0",
                        textTransform: "none",
                      }}
                    >
                      {isCustomOption ? (
                        hasConfirmedCustomTip ? (
                          Number(selectedTip.import).toLocaleString(
                            coin.locale || "es-ES",
                            {
                              style: "currency",
                              currency: coin.currency || "EUR",
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 2,
                            },
                          )
                        ) : (
                          <EditIcon
                            sx={{
                              fontSize: 22,
                              color: isSelected ? "#ffffff" : "#555555",
                            }}
                          />
                        )
                      ) : (
                        tip.import.toLocaleString(coin.locale || "es-ES", {
                          style: "currency",
                          currency: coin.currency || "EUR",
                          maximumFractionDigits: 0,
                        })
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
            <div className="form-field-error-slot" aria-live="polite">
              {formErrors.amount ? (
                <p className="form-field-error" role="alert">
                  {t("form.validation.amountRequired")}
                </p>
              ) : null}
            </div>
            {(() => {
              const visibleChips = buildVisibleDestinoChips(
                nuclioNegocio,
                activeUnidades,
                tipeds,
              );
              if (!isFromBusiness || !shouldShowChipSelector(visibleChips)) {
                return null;
              }
              return (
                <div
                  ref={destinoSectionRef}
                  className={`w-100 mt-2 ${
                    formErrors.destino ? "form-section-error" : ""
                  }`}
                >
                  <strong className="d-block mb-1 form-destino-label">
                    {t("form.destinoLabel")}{" "}
                    <span className="form-required-mark">*</span>
                  </strong>
                  <div className="tiped-selector-container">
                    <div className="tiped-chips-scroll">
                      {nuclioNegocio?.propinaDirecta &&
                        tipeds.map((tiped) => {
                          const initials = `${tiped.nombre?.[0] || ""}${
                            tiped.apellidos?.[0] || ""
                          }`.toUpperCase();
                          const isSelected = selectedTiped?._id === tiped._id;

                          return (
                            <div
                              key={tiped._id}
                              className={`tiped-chip ${
                                isSelected ? "tiped-chip-selected" : ""
                              }`}
                              onClick={() => {
                                event({
                                  action: "select_tiped",
                                  category: "Propina Directa",
                                  label: `${tiped.nombre} ${tiped.apellidos}`,
                                });
                                setSelectedTiped(tiped);
                                setSelectedUnidadTrabajo(null);
                                setSelectedRepartoColectivo(false);
                                setHasSelectedDestino(true);
                              }}
                            >
                              <div
                                className="tiped-chip-avatar"
                                style={tipedAvatarStyle}
                              >
                                {tiped.photo ? (
                                  <img
                                    src={tiped.photo}
                                    alt={`${tiped.nombre} ${tiped.apellidos}`}
                                  />
                                ) : (
                                  <span style={tipedAvatarInitialsStyle}>
                                    {initials}
                                  </span>
                                )}
                              </div>
                              <div className="tiped-chip-content">
                                <div className="tiped-chip-name">
                                  {tiped.alias ||
                                    `${tiped.nombre} ${tiped.apellidos}`}
                                </div>
                                {tiped.personalMessage && (
                                  <div className="tiped-chip-message">
                                    "{tiped.personalMessage}"
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}

                      {nuclioNegocio?.mostrarUnidades &&
                        activeUnidades.map((unidad) => {
                          const isSelected =
                            String(selectedUnidadTrabajo) ===
                            String(unidad.cargoId);
                          const unidadNombre = getTextByLanguage(
                            typeof unidad.name === "string"
                              ? { es: unidad.name }
                              : unidad.name || {},
                          );
                          const unidadAvatarUrl = resolveStoredMediaUrl(
                            unidad.avatar,
                          );

                          return (
                            <div
                              key={unidad.cargoId}
                              className={`tiped-chip tiped-chip-destino ${
                                isSelected ? "tiped-chip-selected" : ""
                              }`}
                              onClick={() => {
                                event({
                                  action: "select_unidad_trabajo",
                                  category: "Propina Directa",
                                  label: unidadNombre,
                                });
                                setSelectedUnidadTrabajo(unidad.cargoId);
                                setSelectedRepartoColectivo(false);
                                setSelectedTiped(null);
                                setHasSelectedDestino(true);
                              }}
                            >
                              <div className="tiped-chip-avatar equipo-avatar">
                                {unidadAvatarUrl ? (
                                  <img
                                    src={unidadAvatarUrl}
                                    alt={unidadNombre}
                                  />
                                ) : (
                                  <span>🏢</span>
                                )}
                              </div>
                              <div className="tiped-chip-content">
                                <div className="tiped-chip-name">
                                  {unidadNombre}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                      {nuclioNegocio?.repartoColectivo && (
                        <div
                          className={`tiped-chip tiped-chip-destino ${
                            selectedRepartoColectivo
                              ? "tiped-chip-selected"
                              : ""
                          }`}
                          onClick={() => {
                            event({
                              action: "select_reparto_colectivo",
                              category: "Propina Directa",
                              label: "A todos",
                            });
                            setSelectedRepartoColectivo(true);
                            setSelectedUnidadTrabajo(null);
                            setSelectedTiped(null);
                            setHasSelectedDestino(true);
                          }}
                        >
                          <div className="tiped-chip-avatar equipo-avatar">
                            <img
                              src={repartoColectivoIconSrc}
                              alt={
                                repartoColectivoIconUrl
                                  ? repartoColectivoLabelText
                                  : businessLogoForHeader
                                    ? business.nombre_comercial ||
                                      businessData?.nombre_comercial ||
                                      "Logo"
                                    : "Tipify"
                              }
                              style={{
                                maxHeight: "80px",
                                objectFit: "contain",
                              }}
                            />
                          </div>
                          <div className="tiped-chip-content">
                            <div className="tiped-chip-name">
                              {repartoColectivoLabelText}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="form-field-error-slot" aria-live="polite">
                    {formErrors.destino ? (
                      <p className="form-field-error" role="alert">
                        {t("form.validation.destinoRequired")}
                      </p>
                    ) : null}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Campana de propinas: encima de los botones de pago (exprés y one-tap pagan desde aquí) */}
        {!(isFromEvent && eventData?.soloRatings) &&
          isFromBusiness &&
          business?.campanaActiva && (
            <div className="px-3">
              <CampanaPropinaBlock
                value={campana}
                onChange={setCampana}
                accent={avatarSiglasColor}
              />
            </div>
          )}

        {/* Checkboxes - Solo mostrar si NO es evento solo de ratings */}
        {!(isFromEvent && eventData?.soloRatings) && (
          <div className="d-flex flex-column align-items-center">
            <div className="row d-flex align-items-center my-1">
              <div className="col-md-12">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={payFees}
                      onChange={() => {
                        const newValue = !payFees;
                        event({
                          action: "toggle_pay_fees",
                          category: "Donaciones",
                          label: "Pantalla Inicial",
                          value: newValue ? 1 : 0,
                        });
                        setPayFees(newValue);
                      }}
                      style={{ paddingTop: "0px", paddingBottom: "0px" }}
                    />
                  }
                  label={
                    <span>
                      {t("form.checkboxes.payFeesAmount", {
                        amount: calcularTasaFinal(
                          selectedTip?.import,
                          tasa,
                        ).toLocaleString(coin.locale || "es-ES", {
                          style: "currency",
                          currency: coin.currency || "EUR",
                        }),
                      })}{" "}
                      <span className="heartbeat">💚</span>
                    </span>
                  }
                  sx={{
                    "& .MuiTypography-root": {
                      fontSize: "13px",
                      opacity: "0.75",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        )}
        {/* Botón Continuar - Ocultar si es evento solo de ratings */}
        {!(isFromEvent && eventData?.soloRatings) && (
          <div className="d-flex flex-column align-items-center px-3 pb-2 pt-1">
            <div className="col-md-12 d-flex flex-column align-items-center">
              {oneTapButton}
              {oneTapState === "failed" && oneTapMessage && (
                <p
                  className="form-express-error"
                  role="alert"
                  style={{ marginBottom: "10px" }}
                >
                  {oneTapMessage}
                </p>
              )}
              {expressEnabled ? (
                <WalletExpressPay
                  amountCents={expressAmountCents}
                  label={t("express.totalLabel", {
                    name: business?.nombre_comercial || "Tipify",
                  })}
                  isFormValid={!!expressValidation?.isValid}
                  onInvalidClick={() =>
                    showFormErrors(getFormValidation().errors)
                  }
                  saveTip={handleExpressSaveTip}
                  onPaid={handleExpressPaid}
                  onAvailabilityChange={setWalletReady}
                  timeoutMs={
                    expressMode === "diagnostic" ? null : EXPRESS_TIMEOUT_MS
                  }
                  diagnostic={expressMode === "diagnostic"}
                  disabled={oneTapProcessing}
                  onProcessingChange={setWalletProcessing}
                  extraDiagnostics={[
                    ["Perfil de pago (un toque)", paymentProfileDiag],
                    [
                      "Botón un toque",
                      showOneTap
                        ? `visible (${oneTapState})`
                        : oneTapState === "failed"
                          ? `oculto tras fallo: ${oneTapMessage || ""}`
                          : "no",
                    ],
                  ]}
                >
                  {walletReady || showOneTap ? (
                    <button
                      type="button"
                      className="form-express-other"
                      disabled={oneTapProcessing || walletProcessing}
                      onClick={() => {
                        event({
                          action: "click_otros_metodos",
                          category: "Formulario",
                          label: "Pantalla Inicial",
                        });
                        handleSubmit();
                      }}
                    >
                      {t("express.otherMethods")}
                    </button>
                  ) : (
                    continueButton
                  )}
                </WalletExpressPay>
              ) : (
                continueButton
              )}
              {isFromBusiness && business?.tpvLlamadaActiva && (
                <TpvCallButton
                  onClick={(ubicacion) => {
                    event({
                      action: "click_pagar_datafono",
                      category: "Formulario",
                      label: "Pantalla Inicial",
                    });
                    handleSubmit({ viaTpv: true, ubicacion });
                  }}
                />
              )}
              <p className="form-continue-legal">
                {t("form.continueLegal.prefix")}{" "}
                <button
                  type="button"
                  className="form-continue-legal-link"
                  onClick={() => {
                    event({
                      action: "open_terms_modal",
                      category: "Términos",
                      label: "Pantalla Inicial",
                      value: 1,
                    });
                    openLegalModal(t("form.termsModal.title"), textLegal);
                  }}
                >
                  {t("form.continueLegal.terms")}
                </button>{" "}
                {t("form.continueLegal.middle")}{" "}
                <button
                  type="button"
                  className="form-continue-legal-link"
                  onClick={() => {
                    event({
                      action: "open_privacy_modal",
                      category: "Privacidad",
                      label: "Pantalla Inicial",
                      value: 1,
                    });
                    openLegalModal(
                      t("success.email.privacyModal.title"),
                      t("success.email.privacyModal.content"),
                      { plainText: true },
                    );
                  }}
                >
                  {t("form.continueLegal.privacy")}
                </button>
              </p>
            </div>
          </div>
        )}

        <PoweredByTipify />
      </Container>

      {(() => {
        const currencySymbol =
          (0)
            .toLocaleString(coin?.locale || "es-ES", {
              style: "currency",
              currency: coin?.currency || "EUR",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
            .replace(/[\d\s.,]/g, "")
            .trim() || "€";
        const tipDraftError = getCustomTipAmountError(customTipDraft);
        const tipDraftValid = isValidCustomTipAmount(customTipDraft);
        const tipDraftHasValue = customTipDraft !== "";
        const tipFieldState = tipDraftError
          ? "error"
          : tipDraftValid
            ? "valid"
            : "empty";
        const tipHelperText = tipDraftError
          ? tipDraftError === "min"
            ? t("form.customTip.errorMin", {
                min: MIN_CUSTOM_TIP,
                currency: currencySymbol,
              })
            : tipDraftError === "max"
              ? t("form.customTip.errorMax", {
                  max: MAX_CUSTOM_TIP,
                  currency: currencySymbol,
                })
              : t("form.customTip.invalid", {
                  min: MIN_CUSTOM_TIP,
                  max: MAX_CUSTOM_TIP,
                  currency: currencySymbol,
                })
          : t("form.customTip.helper", {
              min: MIN_CUSTOM_TIP,
              max: MAX_CUSTOM_TIP,
              currency: currencySymbol,
            });
        const confirmLabel = tipDraftValid
          ? t("form.customTip.confirmAmount", {
              amount: formatCustomTipAmount(
                customTipDraft,
                coin?.locale || "es-ES",
              ),
              currency: currencySymbol,
            })
          : t("form.customTip.confirm");

        return (
          <Dialog
            open={customTipModalOpen}
            onClose={handleCustomTipModalCancel}
            fullWidth
            maxWidth="xs"
            disableAutoFocus
            disableRestoreFocus
            PaperProps={{
              className: "custom-tip-modal-paper",
            }}
            slotProps={{
              backdrop: {
                className: "custom-tip-modal-backdrop",
              },
              transition: {
                onEntered: () => {
                  customTipInputRef.current?.focus({ preventScroll: true });
                },
              },
            }}
          >
            <DialogTitle className="custom-tip-modal-title">
              {t("form.customTip.modalTitle")}
            </DialogTitle>
            <DialogContent className="custom-tip-modal-content">
              <div
                className={`custom-tip-amount-field custom-tip-amount-field--${tipFieldState}${
                  customTipInputFocused
                    ? " custom-tip-amount-field--focused"
                    : ""
                }`}
                onClick={() => customTipInputRef.current?.focus()}
              >
                <input
                  ref={customTipInputRef}
                  autoFocus
                  className="custom-tip-amount-input"
                  inputMode="decimal"
                  autoComplete="off"
                  value={customTipDraft}
                  aria-label={t("form.customTip.modalTitle")}
                  aria-invalid={Boolean(tipDraftError)}
                  aria-describedby="custom-tip-helper"
                  onFocus={() => setCustomTipInputFocused(true)}
                  onBlur={() => setCustomTipInputFocused(false)}
                  onChange={handleCustomTipDraftChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      handleCustomTipModalConfirm();
                    }
                  }}
                />
                <div className="custom-tip-amount-display" aria-hidden="true">
                  <span
                    className={
                      tipDraftHasValue
                        ? "custom-tip-amount-value"
                        : "custom-tip-amount-value custom-tip-amount-value--placeholder"
                    }
                  >
                    {tipDraftHasValue ? customTipDraft : "0"}
                  </span>
                  {customTipInputFocused && (
                    <span className="custom-tip-caret" />
                  )}
                  <span
                    className={`custom-tip-currency${
                      tipDraftHasValue ? " custom-tip-currency--filled" : ""
                    }`}
                  >
                    {currencySymbol}
                  </span>
                </div>
              </div>
              <p
                id="custom-tip-helper"
                className={`custom-tip-helper${
                  tipDraftError ? " custom-tip-helper--error" : ""
                }`}
                aria-live="polite"
              >
                {tipHelperText}
              </p>
            </DialogContent>
            <DialogActions className="custom-tip-modal-actions">
              <Button
                onClick={handleCustomTipModalConfirm}
                variant="contained"
                className="custom-tip-modal-btn-confirm"
                disabled={!tipDraftValid}
              >
                {confirmLabel}
              </Button>
              <button
                type="button"
                onClick={handleCustomTipModalCancel}
                className="custom-tip-modal-btn-cancel"
              >
                {t("form.customTip.cancel")}
              </button>
            </DialogActions>
          </Dialog>
        );
      })()}
    </>
  );
};

export default FormScreen;
